import React, {useCallback, useState, useRef} from 'react';
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  ConnectionLineType,
  useNodesState,
  useEdgesState,
  ConnectionMode,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import './NodeTypes/custom-nodes.css';
import StepNode from './NodeTypes/StepNode';
import DecisionNode from './NodeTypes/DecisionNode';
import GroupNode from './NodeTypes/GroupNode';
import TerminalNode from './NodeTypes/TerminalNode';
import {Box, Paper, Drawer, IconButton, styled, useTheme} from '@mui/material';
import ProcessNode from './NodeTypes/ProcessNode';
import {selectAllMaps} from 'app/store/operatingModel/operatingModelSlice';
import {
  initDefaultZoom,
  selectCurrentMapXL,
  selectSelectedProcess,
  selectSelectedProcessMapNodes,
  setCurrentMapXL,
} from 'app/store/appState/appStateSlice';
import {useDispatch, useSelector} from 'react-redux';
import {transformData} from '../transformData';
import PropTypes from 'prop-types';
import StepDetailsPopUp from 'components/StepDetails/StepDetailsPopUp';
import {Close} from '@mui/icons-material';
import CustomEdge from './CustomEdge';
import GuideNode from './NodeTypes/GuideNode';
import grips from 'assets/images/grips180.png';
import {EmptyState} from 'components/EmptyState/EmptyState';

const nodeTypes = {
  task: StepNode,
  decision: DecisionNode,
  group: GroupNode,
  terminal: TerminalNode,
  process: ProcessNode,
  guide: GuideNode,
};

const edgeTypes = {
  custom: CustomEdge,
};

// Helper methods to retrieve all group nodes and edges from the data
const getGroupElements = (nodes, edges) => {
  const groupNodes = [];
  const groupEdges = [];
  nodes.forEach((node) => {
    if (node.type == 'group') {
      groupNodes.push(node);
    }
  });
  edges.forEach((edge) => {
    if (edge.type == 'group') {
      groupEdges.push(edge);
    }
  });
  return {groupNodes, groupEdges};
};

const getLayoutedElements = (nodes, edges) => {
  let hasZeroZero = false;
  nodes.map((n) => {
    if (n.data.position_y === 0 && n.data.position_x === 0) hasZeroZero = true;
  });
  const cellWidth = 250;
  const cellHeight = 200;
  const groupSpacing = 10;
  const spacingX = 80;
  const spacingY = 100;
  const marginX = 60;
  const marginY = hasZeroZero ? 200 : 50;
  //const groupHeight = marginY + maxRow * cellHeight + (maxRow - 1) * spacingY;
  let maxWidth = 0;
  let groupPositionX = 0;

  const {groupNodes, groupEdges} = getGroupElements(nodes, edges);

  let maxGroupsRow = 1;
  nodes.forEach((node) => {
    const row = Number(node.data.position_y);
    if (row > maxGroupsRow) {
      maxGroupsRow = row;
    }
  });
  const maxHeight = maxGroupsRow * spacingY + (maxGroupsRow + 1) * cellHeight + 2 * marginY;

  groupNodes.forEach((groupNode) => {
    const groupId = groupNode.id;
    let maxWidthPerGroup = 1;
    nodes.forEach((node) => {
      if (node.type == 'group' || node.parentNode != groupId) {
        return;
      }
      const row = Number(node.data.position_y);
      const col = Number(node.data.position_x);

      if (col > maxWidthPerGroup) {
        maxWidthPerGroup = col;
      }
      node.position = {x: marginX + col * (cellWidth + spacingX), y: marginY + row * (cellHeight + spacingY)};

      return node;
    });
    groupNode.height = maxHeight;
    maxWidthPerGroup = Number(maxWidthPerGroup) + 1;
    groupNode.width = 2 * marginX + maxWidthPerGroup * cellWidth + (maxWidthPerGroup - 1) * spacingX;
    groupNode.data.width = groupNode.width;
    groupNode.data.height = maxHeight;
    groupNode.position = {x: groupPositionX, y: 0};
    groupPositionX += groupNode.width + groupSpacing;
    maxWidth += groupNode.width;
  });
  edges = edges.concat(groupEdges);
  if (groupNodes.length == 0) {
    nodes.forEach((node) => {
      const row = Number(node.data.position_y);
      const col = Number(node.data.position_x);

      node.position = {x: marginX + col * (cellWidth + spacingX), y: 50 + row * (cellHeight + spacingY)};

      return node;
    });
  }
  return {nodes, edges, maxWidth, maxHeight, maxGroupsRow};
};
const StyledPaper = styled(Paper)({
  //scrollbarWidth: 'none' /* Firefox */,
  overflow: 'scroll',
  '&::-webkit-scrollbar': {
    width: '12px',
    height: '12px',
  } /* Chrome */,
  '&::-webkit-scrollbar-track': {
    background: '#DDDFE3',
    maxWidth: '8px',
  } /* Chrome */,
  '&::-webkit-scrollbar-thumb': {
    backgroundImage: `url(${grips})`,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundColor: 'white',
    minHeight: '50%',
    maxHeight: '50%',
    border: '2px solid #DDDFE3',
    minWidth: '12px',
    maxWidth: '12px',
    transform: 'rotate(180deg)',
  },
});

const LayoutFlow = ({subProcess, zoomLevel}) => {
  const processMaps = useSelector(selectAllMaps);
  const isMapXL = useSelector(selectCurrentMapXL);
  const selectedProcess = useSelector(selectSelectedProcess);
  const selectedMapNodes = useSelector(selectSelectedProcessMapNodes);
  const [edgeFlag, setEdgeFlag] = React.useState(subProcess);
  const [nodeFlag, setNodeFlag] = React.useState(subProcess);
  const newProcess = React.useRef(selectedProcess?.name);
  const newXLMap = React.useRef(isMapXL);
  const theme = useTheme();
  const dispatch = useDispatch();

  const {allNodes: nodesData, allEdges: edgesData} = transformData(
    processMaps,
    selectedMapNodes,
    selectedProcess,
    subProcess,
  );
  const {
    nodes: layoutedNodes,
    edges: layoutedEdges,
    maxWidth,
    maxHeight,
    maxGroupsRow,
  } = getLayoutedElements(nodesData, edgesData);

  const [nodes, setNodes, onNodesChange] = useNodesState(layoutedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(layoutedEdges);

  const reactFlowInstance = useReactFlow();

  // Scroll paper on Drag
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (event) => {
    setIsDragging(true);
    setStartX(event.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
  };

  const handleMouseMove = (event) => {
    if (!isDragging) return;
    event.preventDefault();
    const x = event.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 1.2; // Adjust the scroll speed here

    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const hasMaps = () => {
    return processMaps?.some((map) => map.process_name === selectedProcess.process_id);
  };

  React.useEffect(() => {
    if (!isMapXL && maxGroupsRow >= 8) {
      dispatch(setCurrentMapXL(true));
    } else if (isMapXL && maxGroupsRow < 8) {
      dispatch(setCurrentMapXL(false));
    }
    if (isMapXL && isMapXL != newXLMap.current) {
      dispatch(initDefaultZoom(0.6));
      newXLMap.current = isMapXL;
    }
    if (reactFlowInstance.getViewport().zoom !== zoomLevel) {
      reactFlowInstance.setViewport({x: 0, y: 0, zoom: zoomLevel});
    }
  }, [zoomLevel, reactFlowInstance, maxGroupsRow, isMapXL, dispatch]);

  //loading nodes w/ clicking in between sub processes
  React.useEffect(() => {
    if (layoutedNodes.length > 0 && nodeFlag !== subProcess) {
      setNodes(layoutedNodes);
      setNodeFlag(subProcess);
    }
  }, [layoutedNodes, setNodes, nodeFlag, setNodeFlag, subProcess]);

  //load data
  React.useEffect(() => {
    if (selectedProcess?.name && newProcess.current !== selectedProcess?.name) {
      setNodes(layoutedNodes);
      setEdges(layoutedEdges);

      newProcess.current = selectedProcess?.name;
    } else if (nodes?.length === 0 && processMaps?.some((map) => map.process_name === selectedProcess.process_id)) {
      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
    } else if (!nodes?.some((node) => node.type === 'group') && layoutedNodes?.some((node) => node.type === 'group')) {
      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
    }
  }, [selectedProcess, nodes, layoutedEdges, layoutedNodes, setNodes, setEdges, newProcess, processMaps]);

  React.useEffect(() => {
    if (layoutedEdges.length > 0 && edgeFlag !== subProcess) {
      setEdges(layoutedEdges);
      setEdgeFlag(subProcess);
    }
  }, [layoutedEdges, setEdges, edgeFlag, setEdgeFlag, subProcess]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({...params, type: ConnectionLineType.SmoothStep, animated: true}, eds)),
    [setEdges],
  );

  const [nodeDetails, setNodeDetails] = useState();

  const [open, setOpen] = useState(false);
  const toggleDrawer = () => {
    setOpen(!open);
  };

  const displayStepDetails = (event, node) => {
    if (node.type == 'task') {
      setNodeDetails(node.data);
      setOpen(true);
    }
  };

  return (
    <>
      {(nodes.length > 0) & (edges.length > 0) && hasMaps() ? (
        <Box sx={{backgroundColor: 'white'}}>
          <StyledPaper
            sx={{
              maxWidth: '100%',
              overflow: 'scroll',
              background: theme.palette.grey[100],
              cursor: isDragging ? 'grabbing' : 'grab',
            }}
            id='scroll-paper-container'
            ref={containerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            <Box
              sx={{
                width: maxWidth * zoomLevel + 30,
                height: maxHeight * zoomLevel + 30,
                margin: '0px',
              }}
            >
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                defaultViewport={{x: 0, y: 0, zoom: zoomLevel}}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeClick={displayStepDetails}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                zoomOnDoubleClick={false}
                connectionLineType={ConnectionLineType.SmoothStep}
                onlyRenderVisibleElements={'false'}
                proOptions={{hideAttribution: true}}
                connectionMode={ConnectionMode.Loose}
                preventScrolling={false}
                zoomOnScroll={false}
                panOnDrag={false}
                onLoad={(instance) => (reactFlowInstance.current = instance)}
              ></ReactFlow>
            </Box>
            <Drawer
              anchor='right'
              width={1000}
              open={open}
              onClose={toggleDrawer}
              BackdropProps={{
                invisible: true,
              }}
            >
              <Paper
                sx={{
                  borderLeft: `1px solid ${theme.palette.primary.main}`,
                  width: '100%',
                  height: '100%',
                  borderRadius: 0,
                  boxShadow: '-4px 0px 10px 0px rgba(0, 0, 0, 0.25)',
                  overflow: 'auto',
                }}
              >
                <Box width='100%' display='flex' justifyContent='flex-end'>
                  <IconButton
                    onClick={() => {
                      setOpen(false);
                    }}
                  >
                    <Close />
                  </IconButton>
                </Box>
                <StepDetailsPopUp nodeDetails={nodeDetails} />
              </Paper>
            </Drawer>
          </StyledPaper>
        </Box>
      ) : (
        <Box width='100%' height='15vw' display='flex' alignItems='center' justifyContent='center'>
          <EmptyState
            icon={<Close color='primary' fontSize='medium' />}
            title='Data not defined'
            subtitle='This process has not been loaded yet'
          />
        </Box>
      )}
    </>
  );
};

LayoutFlow.propTypes = {
  subProcess: PropTypes.number.isRequired,
  zoomLevel: PropTypes.number.isRequired,
};

export default function FlowChartL2({subProcess, zoomLevel}) {
  return (
    <Box>
      <ReactFlowProvider>
        <LayoutFlow subProcess={subProcess} zoomLevel={zoomLevel} />
      </ReactFlowProvider>
    </Box>
  );
}

FlowChartL2.propTypes = {
  subProcess: PropTypes.number.isRequired,
  zoomLevel: PropTypes.number.isRequired,
};
